import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { useProjects } from '../hooks/useProject';
import { Button } from '../components/ui/button';
import { ArrowLeft, Plus, Trash2, Download, Box, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const ProjectsPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { projects, loading, deleteProject, exportProject } = useProjects();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [projectToDelete, setProjectToDelete] = React.useState(null);

  const handleDelete = async () => {
    if (!projectToDelete) return;
    try {
      await deleteProject(projectToDelete.id);
      toast.success(t('common.success'));
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleExport = async (project) => {
    try {
      const blob = await exportProject(project.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.name.replace(/ /g, '_')}.mcaddon`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(t('common.success'), { description: 'Addon exportado' });
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="projects-page">
      <header className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-gray-900"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.back')}
          </Button>
          <Button
            size="sm"
            onClick={() => navigate('/new-project')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="new-project-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('home.new_project')}
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('projects.title')}</h1>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-24 border-2 border-dashed border-gray-200 rounded-lg">
            <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Box className="w-7 h-7 text-gray-400" strokeWidth={1.5} />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">{t('projects.empty')}</h3>
            <p className="text-sm text-gray-500 mb-6">{t('projects.empty_desc')}</p>
            <Button
              onClick={() => navigate('/new-project')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('home.new_project')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project) => (
              <div
                key={project.id}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-blue-500 hover:shadow-md transition-all cursor-pointer group"
                onClick={() => navigate(`/editor/${project.id}`)}
                data-testid={`project-card-${project.id}`}
              >
                {/* Imagen de portada del proyecto */}
                <div className="aspect-video bg-gradient-to-br from-blue-50 to-white flex items-center justify-center relative border-b border-gray-100">
                  {project.cover_image ? (
                    <img
                      src={project.cover_image}
                      alt={project.name}
                      className="w-full h-full object-contain"
                      data-testid={`project-cover-${project.id}`}
                    />
                  ) : project.texture ? (
                    <img
                      src={project.texture}
                      alt={project.name}
                      style={{ imageRendering: 'pixelated' }}
                      className="w-3/4 h-3/4 object-contain"
                    />
                  ) : (
                    <Box className="w-10 h-10 text-blue-300" strokeWidth={1.5} />
                  )}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExport(project);
                      }}
                      className="p-1.5 bg-white/95 backdrop-blur rounded shadow border border-gray-200"
                      data-testid={`export-project-${project.id}`}
                    >
                      <Download className="w-3.5 h-3.5 text-gray-700" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setProjectToDelete(project);
                        setDeleteDialogOpen(true);
                      }}
                      className="p-1.5 bg-white/95 backdrop-blur rounded shadow border border-gray-200 text-red-500"
                      data-testid={`delete-project-${project.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-base font-semibold text-gray-900 mb-1 truncate">
                    {project.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-3">
                    {t('projects.by')} {project.author}
                  </p>
                  {project.description && (
                    <p className="text-xs text-gray-500 line-clamp-2 mb-3">{project.description}</p>
                  )}
                  <div className="flex gap-1.5 flex-wrap">
                    <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                      {project.category}
                    </span>
                    {project.template_name && (
                      <span className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded">
                        {project.template_name}
                      </span>
                    )}
                    {project.export_version && (
                      <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded font-mono">
                        v{project.export_version}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar proyecto?</AlertDialogTitle>
            <AlertDialogDescription>
              El proyecto "{projectToDelete?.name}" se eliminará permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProjectsPage;
